/**
 * PublicRecipePage — no-login mobile-first recipe page for the household cook.
 *
 * Triggered by /r/<dishKey>?lang=hi|en links shared via WhatsApp / SMS.
 * Backend: GET /api/public/dishes/<dishKey>?lang=<lang> (no auth).
 *
 * Design goals:
 *   • Large fonts (cooks are often middle-aged, kitchen lighting varies).
 *   • Sticky lang-toggle pill at the top so user can flip हिन्दी ⇄ English.
 *   • YouTube embed at the very top after the hero (most cooks watch first).
 *   • Ingredients as a tap-to-tick checklist (localStorage so refresh keeps state).
 *   • Step photos inline with the step text.
 *   • Works fully offline once cached (every API response is cacheable).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ForkKnife, Clock, Users, Check, ArrowSquareOut, Spinner, CheckCircle } from '@phosphor-icons/react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const L = {
  en: {
    description: 'About this dish',
    watchCook: 'Watch & cook',
    searchYT: 'Search recipes on YouTube',
    ingredients: 'Ingredients',
    method: 'Method',
    varieties: 'Varieties & forms',
    tips: 'Cook\u2019s tips',
    serves: (n) => `Serves ${n}`,
    notReady: 'This recipe isn\u2019t ready yet. The household needs to open it inside the app once before sharing.',
    minutes: (n) => `${n} min`,
    swap: 'हिन्दी',
    youOpened: 'No login needed. Tap हिन्दी above to switch language.',
    yourName: 'Your name',
    yourNamePh: 'e.g. Ramu',
    save: 'Save',
    done: 'Done',
    completed: 'Completed at',
    progressFor: (n) => `Cooking as ${n}. Tap "Done" on each step \u2014 the household sees your progress live.`,
  },
  hi: {
    description: 'इस व्यंजन के बारे में',
    watchCook: 'देखें और बनाएँ',
    searchYT: 'YouTube पर रेसिपी खोजें',
    ingredients: 'सामग्री',
    method: 'विधि',
    varieties: 'विभिन्न प्रकार',
    tips: 'रसोई के सुझाव',
    serves: (n) => `${n} लोगों के लिए`,
    notReady: 'यह रेसिपी अभी तैयार नहीं है। पहले इसे ऐप में खोलें, फिर शेयर करें।',
    minutes: (n) => `${n} मिनट`,
    swap: 'English',
    youOpened: 'लॉगिन की ज़रूरत नहीं। ऊपर English दबाकर भाषा बदलें।',
    yourName: 'आपका नाम',
    yourNamePh: 'जैसे रामू',
    save: 'सहेजें',
    done: 'हो गया',
    completed: 'पूरा हुआ',
    progressFor: (n) => `${n} के रूप में पका रहे हैं। हर चरण पर "हो गया" दबाएँ \u2014 घर पर देखेंगे।`,
  },
};

export default function PublicRecipePage() {
  const { dishKey } = useParams();
  const [params, setParams] = useSearchParams();
  const lang = params.get('lang') === 'hi' ? 'hi' : 'en';
  const t = L[lang];

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Cook progress context — populated from share URL query params when the
  // household clicks "Share with Cook" from a planner chip. With these set,
  // tapping the LAST step rolls up into a single "meal is ready" alert.
  const planId = params.get('plan_id') || '';
  const slot = params.get('slot') || '';
  const dateStr = params.get('date') || '';
  const tracksProgress = !!(planId && slot);

  // Cook name — asked once, saved to localStorage.
  const [cookName, setCookName] = useState(() => {
    try { return localStorage.getItem('cook-name') || ''; }
    catch { return ''; }
  });
  const [cookNameDraft, setCookNameDraft] = useState('');

  // Per-step completion state (synced with backend).
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [stepBusy, setStepBusy] = useState(-1);

  // Tap-to-tick ingredient state (persisted to localStorage per dish).
  const tickedKey = `recipe-ticked.${dishKey}`;
  const [ticked, setTicked] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(tickedKey) || '[]')); }
    catch { return new Set(); }
  });
  useEffect(() => {
    try { localStorage.setItem(tickedKey, JSON.stringify([...ticked])); } catch { /* ignore */ }
  }, [ticked, tickedKey]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${BACKEND}/api/public/dishes/${dishKey}?lang=${lang}`);
      setData(r.data);
      // Hydrate progress if available.
      if (tracksProgress) {
        try {
          const pr = await axios.get(`${BACKEND}/api/public/cook/progress`,
            { params: { dish_key: dishKey, plan_id: planId, slot } });
          const items = (pr.data?.items) || [];
          const mine = items[0]; // same (plan_id, slot, dish_key) is unique
          if (mine?.completed_steps) setCompletedSteps(new Set(mine.completed_steps));
        } catch { /* progress is best-effort */ }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || t.notReady);
    } finally { setLoading(false); }
  }, [dishKey, lang, t.notReady, tracksProgress, planId, slot]);

  useEffect(() => { load(); document.documentElement.lang = lang; }, [load, lang]);

  const saveCookName = (e) => {
    e.preventDefault();
    const v = (cookNameDraft || '').trim();
    if (!v) return;
    try { localStorage.setItem('cook-name', v); } catch { /* ignore */ }
    setCookName(v);
  };

  const markStepDone = async (idx) => {
    if (!data?.instructions?.length || stepBusy === idx || completedSteps.has(idx)) return;
    setStepBusy(idx);
    try {
      await axios.post(`${BACKEND}/api/public/cook/progress`, {
        dish_key: dishKey,
        dish_name: data.name_en || data.name,
        plan_id: planId || null,
        slot: slot || null,
        date: dateStr || null,
        cook_name: cookName || null,
        step_index: idx,
        total_steps: data.instructions.length,
      });
      setCompletedSteps(prev => new Set([...prev, idx]));
    } catch (e) {
      // soft-fail: user still sees recipe; tick disappears so they can retry
    } finally { setStepBusy(-1); }
  };

  const swapLang = () => setParams({ lang: lang === 'hi' ? 'en' : 'hi' }, { replace: true });

  const toggleTick = (i) => {
    setTicked(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const heroAbs = data?.hero_image_url
    ? (data.hero_image_url.startsWith('http') ? data.hero_image_url : `${BACKEND}${data.hero_image_url}`)
    : '';

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" lang={lang} data-testid="public-recipe-page">
      {/* Sticky lang switch */}
      <div className="sticky top-0 z-20 bg-neutral-950/95 backdrop-blur-md border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <ForkKnife size={20} className="text-[#FF6B35] flex-shrink-0" />
          <span className="text-sm font-semibold truncate">{data?.name || dishKey}</span>
        </div>
        <button onClick={swapLang} data-testid="public-recipe-lang-swap"
          className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#FF6B35]/20 text-[#FF6B35] border border-[#FF6B35]/40 flex-shrink-0">
          {t.swap}
        </button>
      </div>

      {loading && (
        <div className="p-10 flex flex-col items-center gap-3 text-neutral-400" data-testid="public-recipe-loading">
          <Spinner size={32} className="animate-spin text-[#FF6B35]" />
          <p className="text-sm">…</p>
        </div>
      )}

      {error && (
        <div className="m-5 rounded-xl bg-amber-950/40 border border-amber-700/40 text-amber-200 p-4 text-sm" data-testid="public-recipe-error">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <div className="max-w-2xl mx-auto pb-24">
          {/* Hero */}
          {heroAbs && (
            <div className="w-full aspect-video bg-neutral-900 overflow-hidden">
              <img src={heroAbs} alt={data.name} className="w-full h-full object-cover" loading="eager" />
            </div>
          )}

          <div className="p-5 space-y-7">
            {/* Title block */}
            <div>
              <h1 className="text-3xl font-bold text-white" data-testid="public-recipe-title">{data.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                {data.cuisine && <span className="px-2.5 py-0.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30">{data.cuisine}</span>}
                {data.diet_type && <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">{data.diet_type}</span>}
                {((data.prep_time_mins || 0) + (data.cook_time_mins || 0)) > 0 && (
                  <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/10 text-white/80">
                    <Clock size={14} /> {t.minutes((data.prep_time_mins || 0) + (data.cook_time_mins || 0))}
                  </span>
                )}
                {data.servings && (
                  <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/10 text-white/80">
                    <Users size={14} /> {t.serves(data.servings)}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-neutral-500 mt-3">{t.youOpened}</p>
            </div>

            {/* Cook name prompt — shown only when this page tracks progress
                (i.e. opened from a planner-chip share link) AND no cook name
                stored yet. Once entered, hidden forever per-device. */}
            {tracksProgress && !cookName && (
              <form onSubmit={saveCookName} className="rounded-2xl p-4 bg-[#FF6B35]/10 border border-[#FF6B35]/30 space-y-2"
                data-testid="public-recipe-cook-name-form">
                <label className="text-base font-semibold text-orange-200">{t.yourName}</label>
                <div className="flex gap-2">
                  <input value={cookNameDraft} onChange={e => setCookNameDraft(e.target.value)}
                    placeholder={t.yourNamePh}
                    data-testid="public-recipe-cook-name-input"
                    className="flex-1 px-3 py-2.5 rounded-xl bg-neutral-900 border border-neutral-700 text-white text-base" />
                  <button type="submit"
                    data-testid="public-recipe-cook-name-save"
                    className="px-4 py-2.5 rounded-xl bg-[#FF6B35] text-white text-sm font-bold">
                    {t.save}
                  </button>
                </div>
              </form>
            )}
            {tracksProgress && cookName && (
              <p className="text-xs text-orange-300/80" data-testid="public-recipe-cook-banner">
                {t.progressFor(cookName)}
              </p>
            )}
            {data.description && (
              <section>
                <h2 className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-2">{t.description}</h2>
                <p className="text-base text-neutral-200 leading-relaxed">{data.description}</p>
              </section>
            )}

            {/* Video */}
            {data.video && (
              <section>
                <h2 className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-2">{t.watchCook}</h2>
                {data.video.embed_url ? (
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-black border border-white/5">
                    <iframe src={data.video.embed_url} title={data.video.title} className="absolute inset-0 w-full h-full"
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                      data-testid="public-recipe-yt-embed" />
                  </div>
                ) : (
                  <a href={data.video.search_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-600/40 text-base font-semibold"
                    data-testid="public-recipe-yt-search">
                    <ArrowSquareOut size={18} /> {t.searchYT}
                  </a>
                )}
                {data.video.title && (
                  <p className="text-xs text-neutral-500 mt-2">{data.video.channel ? `${data.video.channel} · ` : ''}{data.video.title}</p>
                )}
              </section>
            )}

            {/* Ingredients (tap-to-tick) */}
            {data.ingredients?.length > 0 && (
              <section>
                <h2 className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-3">{t.ingredients}</h2>
                <ul className="space-y-2">
                  {data.ingredients.map((ing, i) => {
                    const isTicked = ticked.has(i);
                    return (
                      <li key={i}>
                        <button onClick={() => toggleTick(i)}
                          data-testid={`public-recipe-ingredient-${i}`}
                          className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition ${isTicked ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200' : 'bg-white/5 border-white/10 text-neutral-200'}`}>
                          <span className={`flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center mt-0.5 ${isTicked ? 'bg-emerald-500 border-emerald-500' : 'border-neutral-600'}`}>
                            {isTicked && <Check size={14} weight="bold" className="text-white" />}
                          </span>
                          <span className="flex-1 text-base">
                            <b>{ing.quantity}{ing.unit ? ` ${ing.unit}` : ''}</b> {ing.name}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {/* Method */}
            {data.instructions?.length > 0 && (
              <section>
                <h2 className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-3">{t.method}</h2>
                <ol className="space-y-5">
                  {data.instructions.map((step, i) => {
                    const img = data.step_images?.find(s => s.index === i + 1);
                    const isDone = completedSteps.has(i);
                    const isBusy = stepBusy === i;
                    return (
                      <li key={i} className="flex gap-3">
                        <span className={`flex-shrink-0 w-9 h-9 rounded-full text-lg font-bold flex items-center justify-center border ${isDone ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-[#FF6B35]/20 border-[#FF6B35]/40 text-[#FF6B35]'}`}>
                          {isDone ? <Check size={18} weight="bold" /> : (i + 1)}
                        </span>
                        <div className="flex-1 space-y-2">
                          <p className={`text-base leading-relaxed ${isDone ? 'text-neutral-400 line-through' : 'text-neutral-200'}`}>{step}</p>
                          {img && (
                            <img src={`${BACKEND}/api/meals/dish-steps/${data.dish_key}_step${i + 1}.jpg`}
                              alt={`${i + 1}`}
                              className="w-full rounded-xl border border-white/10" loading="lazy" />
                          )}
                          {/* "Done" button: only when we have plan_id+slot context
                              (i.e. household actually wants notifications).
                              Without context, this is just a read-only recipe. */}
                          {tracksProgress && !isDone && (
                            <button onClick={() => markStepDone(i)} disabled={isBusy}
                              data-testid={`public-recipe-step-done-${i + 1}`}
                              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 text-sm font-bold disabled:opacity-50">
                              {isBusy ? <Spinner size={14} className="animate-spin" /> : <CheckCircle size={14} weight="fill" />} {t.done}
                            </button>
                          )}
                          {tracksProgress && isDone && (
                            <p className="text-[11px] text-emerald-400/80 font-semibold">✓ {t.done}</p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </section>
            )}

            {/* Varieties */}
            {data.varieties?.length > 0 && (
              <section>
                <h2 className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-3">{t.varieties}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {data.varieties.map((v, i) => (
                    <div key={i} className="p-3 rounded-xl bg-white/5 border border-white/10">
                      <p className="text-base font-semibold text-white">{v.name}</p>
                      <p className="text-sm text-neutral-400 mt-0.5">{v.note}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Tips */}
            {data.tips?.length > 0 && (
              <section className="rounded-2xl bg-amber-500/5 border border-amber-500/20 p-4">
                <h2 className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t.tips}</h2>
                <ul className="text-sm text-amber-100/90 space-y-2">
                  {data.tips.map((tip, i) => (
                    <li key={i} className="flex gap-2"><span>•</span><span>{tip}</span></li>
                  ))}
                </ul>
              </section>
            )}

            <p className="text-center text-[10px] text-neutral-600 pt-6">HomeCommand · Kitchen</p>
          </div>
        </div>
      )}
    </div>
  );
}
